import React, { useState } from 'react';
import ContactHeroImg from '../assets/contact.png';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContact: '',
    serviceInterest: '',
    ageGroup: '',
    urgency: '',
    message: ''
  });

  // initialize submitMessage with a constant advisory message
  const initialMessage = {
    type: 'success',
    text: (
      <>
        This form will open an email draft in a new window.
        <br />
        If a new window does not open, please email us directly at <a href="mailto:armel@speechinnovationnc.org">armel@speechinnovationnc.org</a>.
      </>
    )
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(initialMessage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // mailto: logic for form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // do not clear or update the message state, as it is persistent
    setIsSubmitting(true);

    const recipient = 'armel@speechinnovationnc.org'; // primary recipient email
    const ccRecipient = 'vicky@speechinnovationnc.org'; // cc recipient email
    const subject = `New Inquiry from ${formData.firstName} ${formData.lastName}`;

    // helper function to format form data into a readable string for the email body
    const formatBody = () => {
      let body = `Hello Armel and Vicky,\n\nI am contacting you regarding a new inquiry. My details and interests are below:\n\n`;
      body += `Full Name: ${formData.firstName} ${formData.lastName}\n`;
      body += `Email: ${formData.email}\n`;
      body += `Phone: ${formData.phone || 'N/A'}\n`;
      body += `Preferred Contact: ${formData.preferredContact || 'N/A'}\n`;
      body += `Service Interest: ${formData.serviceInterest || 'N/A'}\n`;
      body += `Age Group: ${formData.ageGroup || 'N/A'}\n`;
      body += `Timeline: ${formData.urgency || 'N/A'}\n\n`;
      body += `--- Message ---\n`;
      body += `${formData.message}\n\n`;
      body += `(Note: Please remember not to include personal health information in this initial email.)`;
      return encodeURIComponent(body);
    };

    const mailtoLink =
      `mailto:${recipient}?` +
      `cc=${ccRecipient}&` +
      `subject=${encodeURIComponent(subject)}&` +
      `body=${formatBody()}`;

    // open the user's default email client/app
    window.open(mailtoLink, '_blank');

    // reset form after successful generation of mailto link
    setTimeout(() => {
      setIsSubmitting(false);

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        preferredContact: '',
        serviceInterest: '',
        ageGroup: '',
        urgency: '',
        message: ''
      });
    }, 100);
  };

  return (
    <>
      {/* banner */}
      <section className="banner">
        <img id="banner-img" src={ContactHeroImg} alt="contact header" />
      </section>

      {/* lead paragraph */}
      <section className="py-4 bg-white">
        <div className="container text-center">
          <p className="lead mx-auto" style={{ maxWidth: 700 }}>
            Whether you have questions or you're ready to schedule, we're here to help.
          </p>
        </div>
      </section>

      {/* contact section */}
      <section className="contact-form-section py-5 bg-light">
        <div className="container">
          <div className="row g-5">
            {/* custom contact form */}
            <div className="col-md-6">
              <div className="mb-3 text-danger fw-bold text-center">
                Please don't include personal health information. We'll follow up securely to learn more about your needs.
              </div>

              {/* === constant message display === */}
              {/* this box always displays the advisory message */}
              {submitMessage && (
                <div
                  className={`alert alert-${submitMessage.type === 'success' ? 'success' : 'danger'} mb-4`}
                  role="alert"
                >
                  <i className={`fas fa-${submitMessage.type === 'success' ? 'info-circle' : 'exclamation-triangle'} me-2`}></i>
                  {submitMessage.text}
                </div>
              )}
              {/* === end constant message display === */}

              <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm">

                {/* all form input fields */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="firstName" className="form-label">
                      First Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="lastName" className="form-label">
                      Last Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="preferredContact" className="form-label">
                      Preferred Contact Method
                    </label>
                    <select
                      className="form-select"
                      id="preferredContact"
                      name="preferredContact"
                      value={formData.preferredContact}
                      onChange={handleInputChange}
                    >
                      <option value="">Select preference</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone Call</option>
                      <option value="text">Text Message</option>
                      <option value="either">Either Email or Phone</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="serviceInterest" className="form-label">
                      Service Interest
                    </label>
                    <select
                      className="form-select"
                      id="serviceInterest"
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleInputChange}
                    >
                      <option value="">Select service</option>
                      <option value="Orofacial Myofunctional Therapy">Orofacial Myofunctional Therapy</option>
                      <option value="Speech Therapy">Speech Therapy</option>
                      <option value="Language Therapy">Language Therapy</option>
                      <option value="Evaluation">Evaluation</option>
                      <option value="Consultation">Consultation</option>
                      <option value="General Info">General Information</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="ageGroup" className="form-label">Age Group</label>
                    <select
                      className="form-select"
                      id="ageGroup"
                      name="ageGroup"
                      value={formData.ageGroup}
                      onChange={handleInputChange}
                    >
                      <option value="">Select age group</option>
                      <option value="early-intervention">Early Intervention (0-3)</option>
                      <option value="preschool">Preschool (3-5)</option>
                      <option value="school-age">School Age (6-12)</option>
                      <option value="teen">Teen (13-18)</option>
                      <option value="adult">Adult (18+)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="urgency" className="form-label">Timeline</label>
                    <select
                      className="form-select"
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                    >
                      <option value="">Select timeline</option>
                      <option value="asap">As soon as possible</option>
                      <option value="within-week">Within a week</option>
                      <option value="within-month">Within a month</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="form-label">
                    Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please tell us how we can help you."
                    required
                  ></textarea>
                  <div className="form-text text-danger">
                    Remember: Please don't include personal health information in this form.
                  </div>
                </div>

                {/* submit button */}
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.email || !formData.message}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Preparing Email...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Open Email Draft
                    </>
                  )}
                </button>

                <div className="text-center mt-3">
                  <small className="text-muted">
                    Submitting this form will open a pre-filled draft in your default email program.
                  </small>
                </div>
              </form>
            </div>

            {/* location info */}
            <div className="col-md-6 text-center">
              <h4>Our Location</h4>
              <p>
                Pineville, NC<br />
                Serving families across the region
              </p>
              <p>
                Email: <a href="mailto:armel@speechinnovationnc.org">armel@speechinnovationnc.org</a>
              </p>
              <p>
                Email: <a href="mailto:vicky@speechinnovationnc.org">vicky@speechinnovationnc.org</a>
              </p>
              <p>
                Phone: <a href="tel:+17048351004">(704) 835-1004</a>
              </p>
              <div className="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3265.0146392869265!2d-80.88793482430913!3d35.08136697278691!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88569d68100ea88d%3A0x6b19df5bcc20a6d!2s600%20Towne%20Centre%20Blvd%20Suite%20201%2C%20Pineville%2C%20NC%2028134!5e0!3m2!1sen!2sus!4v1751293430939!5m2!1sen!2sus"
                  style={{ width: '100%', height: '500px', border: 0, borderRadius: '8px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Speech Innovation NC Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;